/**
 * Service-credential hooks.
 *
 * The issue mutation returns the plaintext token to its caller and invalidates
 * the list — but it deliberately does NOT write the token into the query cache.
 * A cache entry is a copy that outlives the dialog, survives a re-render, and
 * can be read by anything else with the client. The token's whole security
 * model is that it exists once, in one place, for as long as the operator needs
 * to copy it.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useSdkContext } from '../providers/SdkProvider';
import {
  createServiceCredentialsService,
  type IssueServiceCredentialInput,
  type MaskedServiceCredential,
} from '../services/service-credentials.service';

export const serviceCredentialKeys = {
  all: ['service-credentials'] as const,
  list: () => [...serviceCredentialKeys.all, 'list'] as const,
};

export function useServiceCredentials(options?: { enabled?: boolean }) {
  const { client } = useSdkContext();
  const service = createServiceCredentialsService(client);
  return useQuery<MaskedServiceCredential[]>({
    queryKey: serviceCredentialKeys.list(),
    queryFn: () => service.list(),
    enabled: options?.enabled ?? true,
  });
}

export function useIssueServiceCredential() {
  const { client } = useSdkContext();
  const service = createServiceCredentialsService(client);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: IssueServiceCredentialInput) => service.issue(input),
    onSuccess: () => {
      // Refresh the list only. The token in the result is handed to the caller
      // and never stored here — see the file header.
      void queryClient.invalidateQueries({ queryKey: serviceCredentialKeys.all });
    },
  });
}

export function useRevokeServiceCredential() {
  const { client } = useSdkContext();
  const service = createServiceCredentialsService(client);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => service.revoke(id, reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: serviceCredentialKeys.all });
    },
  });
}
